import { formatPhoneNumber as formatNumber, formatAsYouType } from 'phone-numbers'
import Countries from 'countries'

/**
 *
 * @param {number} duration
 * @returns
 */
const formatDuration = (duration) => {
    if (duration === 0) return '00:00:00'
    let hours = 0
    if (!duration || duration === Infinity || duration === -Infinity) return '—'
    let min = Math.floor(duration / 60)
    if (min >= 60) {
        hours = Math.floor(min / 60)
        min %= 60
    }
    const sec = (Math.round(duration % 60)).toFixed(1)
    return `${hours < 10 ? `0${hours}` : hours}:${min < 10 ? `0${min}` : min}:${sec < 10 ? '0' : ''}${sec}`
}

/**
 *
 * @param {string} name
 * @returns
 */
const getFirstAndLastName = (name) => {
    const white_space_index = name.indexOf(' ')
    let first_name = ''
    let last_name = ''
    if (white_space_index > -1) {
        const regex = /[^a-zA-Z0-9_ '-]/g
        first_name = name.substring(0, white_space_index).replace(regex, '')
        last_name = name.substring(white_space_index).replace(regex, '')
    } else {
        first_name = name
        last_name = name
    }

    return { first_name: first_name.trim(), last_name: last_name.trim() }
}

/**
 *
 * @param {string} country_code
 * @returns
 */
const countryName = function (country_code) {
    if (!country_code) {
        return '—'
    }

    const country = Countries.find((x) => x.alpha2 === country_code)
    if (country) return country.name

    return '—'
}

/**
 *
 * @param {object} data
 * @returns
 */
const stringifyAddress = (data) => {
    if (!data) return '—'
    let lines = data.line_1 || ''
    if (data.line_2) lines += ` ${data.line_2}`
    lines = lines.trim()
    const parts = [
        lines,
        data.postal_code,
        data.city,
        data.province,
        countryName(data.country)
    ].filter((x) => x)
    if (parts.length) return parts.join(', ')

    return '—'
}

/**
 *
 * @param {number|string} timestamp
 * @param {Intl.DateTimeFormatOptions} options
 * @returns
 */
const formatTime = function (timestamp, options = {}) {
    if (!timestamp || timestamp === -Infinity || timestamp === Infinity) return '—'
    const formatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        ...options
    }
    if (Date.parse(timestamp)) return (new Date(Date.parse(timestamp))).toLocaleString(undefined, formatOptions)

    return (new Date(timestamp * 1000)).toLocaleString(undefined, formatOptions)
}

/**
 *
 * @param {number} val
 * @param {object} options
 * @returns
 */
const formatDate = function (
    val,
    options = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    }) {
    if (!val) return null
    const date = new Date(val * 1000)
    return date.toLocaleString('en-US', {
        ...{
            day: 'numeric',
            month: 'numeric',
            year: 'numeric'
        },
        ...options
    })
}

/**
 *
 * @param {number} val
 * @param {object} dateOptions
 * @param {object} timeOptions
 * @returns
 */
const formatDateWithTime = function (
    val,
    dateOptions = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    },
    timeOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }) {
    if (!val) return null
    const date = new Date(val * 1000)
    const datePart = formatDate(val, dateOptions)
    const timePart = date.toLocaleTimeString('en-US', timeOptions).replace(' ', '')

    return `${datePart} ${timePart}`
}

/**
 * Formats a given monetary value for display purposes.
 * @param {number} val integer amount in the units specified by options.unitsPerDollar (cents by default)
 * @param {string} prefix example: "$"
 * @param {object} options
 * @param {boolean} options.alwaysShowCents if false, even dollar amounts will be displayed without .00
 * @param {boolean} options.useCommas if true, the number is render'ed with commas every 3 digits (eg. 1,000)
 * @param {number} options.unitsPerDollar input units per dollar: 100 for cents, 100000 for hundred-thousandths
 * @returns
 */
const formatPrice = (val, prefix = '$', options = {}) => {
    const { alwaysShowCents = true, useCommas = false, unitsPerDollar = 100 } = options
    if (!val && val !== 0) {
        return '—'
    }
    const num = Number.parseInt(val)
    if (Number.isNaN(num)) {
        return '—'
    }
    const dollarValue = num / unitsPerDollar
    let value = num % unitsPerDollar === 0 && !alwaysShowCents ? String(dollarValue) : dollarValue.toFixed(2)
    if (useCommas) {
        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    if (prefix) {
        // Ensure negative sign is before the prefix:
        value = value.replace(/-/g, '')
        return num < 0 ? `-${prefix}${value}` : `${prefix}${value}`
    }
    return value
}

/**
 *
 * @param {number} billingPeriod
 * @returns
 */
const formatBillingPeriod = (billingPeriod) => {
    if (!billingPeriod) return '—'
    const billingPeriodAsNumber = Number.parseInt(billingPeriod)
    if (typeof billingPeriodAsNumber !== 'number' || Number.isNaN(billingPeriodAsNumber)) return 'Invalid billing period'

    if (billingPeriodAsNumber === 1) return 'Monthly'
    if (billingPeriodAsNumber === 3) return 'Quarterly'
    if (billingPeriodAsNumber === 12) return 'Annually'

    return `${Math.floor(12 / billingPeriodAsNumber)} times per year`
}

/**
 *
 * @param {object} device
 * @returns
 */
const formatDeviceModelName = (device) => {
    if (!device) return '—'
    if (typeof device !== 'object') return '—'

    return `${device.manufacturer || ''} ${device.name || ''}`.trim()
}

/**
 *
 * @param {string} phoneNumber
 * @param {string} country alpha2 code
 * @returns
 */
const formatPhoneNumber = (phoneNumber, country = 'US') => {
    if (!phoneNumber) return ''
    if (phoneNumber.startsWith('+88351000')) { // iNum
        return `${phoneNumber.slice(0, 4)} (${phoneNumber.slice(4, 8)}) ${phoneNumber.slice(8)}`
    }
    return formatNumber(phoneNumber, country)
}

/**
 *
 * @param {string} number
 * @param {string} country alpha2 code
 * @returns
 */
const formatPhoneNumberAsYouType = (number, country = 'US') => {
    const phone_number = number
    if (!phone_number) return ''
    if (phone_number.startsWith('+88351000')) { // iNum
        return `${phone_number.slice(0, 4)} (${phone_number.slice(4, 8)}) ${phone_number.slice(8)}`
    }

    let formatted = formatAsYouType(phone_number, country)
    if (country === 'US' && formatted.length && formatted[0] !== '+') {
        formatted = `+1 ${formatted}`
    }
    return formatted
}
/**
 *
 * @param {string} number
 * @param {boolean} no_code
 * @param {string} text_value
 * @param {number} text_start
 * @returns
 */
const formatPhoneNumberWithText = (number, no_code, text_value, text_start) => {
    let phone_number = number
    if (!phone_number) return ''
    if (!text_value) return formatPhoneNumber(number)
    phone_number = `${phone_number.slice(
        0,
        text_start
    )}${text_value.toUpperCase()}${phone_number.slice(
        text_start + text_value.length
    )}`
    const r = /^(\+1)([0-9]{0,3})([0-9]{0,3})([0-9]{0,3})([0-9]{0,4})([A-Z]+)([0-9]{0,3})([0-9]{0,3})([0-9]{0,4})$/
    let p = phone_number.match(r)
    if (p) {
        p = p
            .slice(no_code ? 2 : 1)
            .filter((v) => v.length)
        if (p.length) {
            let formatted_value = `${p[0]}`
            if (p[1]) {
                formatted_value += ` (${p[1]})`
            }
            if (p[2]) {
                formatted_value += ` ${p.slice(2).join('-')}`
            }
            return formatted_value.trim()
        }
    }
    return formatPhoneNumber(number)
}

/**
 *
 * @param {object} data
 * @returns
 */
const assemble_display_name = (data) => {
    const default_empty_value = '—'
    if (!data) return default_empty_value
    const name = []
    for (const key of ['prefix', 'first_name', 'middle_name', 'last_name', 'suffix']) {
        if (data[key]) name.push(data[key].trim())
    }
    if (name.length) return name.join(' ')
    if (data.nickname) return data.nickname.trim()

    if (data.company) return data.company.trim()

    return default_empty_value
}

/**
 *
 * @param {string} str
 * @returns
 */
const upperFirstCase = (str) => {
    if (!str) return ''

    if (typeof str !== 'string') {
        str = str.toString()
    }
    let first_char_index = str.length - str.trimStart().length
    if (first_char_index < 0) first_char_index = 0

    return str.slice(0, first_char_index + 1).toUpperCase() + str.slice(first_char_index + 1)
}

/**
 * @param {string} str
 */
const sentenceCase = (str) => {
    return upperFirstCase(str
        .toLowerCase()
        .replace(/_/g, ' '))
}

/**
 *
 */
export {
    formatDuration,
    getFirstAndLastName,
    countryName,
    sentenceCase,
    stringifyAddress,
    formatTime,
    formatPrice,
    formatDeviceModelName,
    formatDate,
    formatDateWithTime,
    formatPhoneNumber,
    formatPhoneNumberAsYouType,
    formatPhoneNumberWithText,
    assemble_display_name,
    upperFirstCase,
    formatBillingPeriod
}
